import { createRouter, createWebHistory } from 'vue-router'
import { authState } from '@/services/googleAuth'

// Preload critical routes for instant navigation
const preloadRoute = (importFn) => {
  const component = importFn()
  // Component will be cached by browser after first load
  return () => component
}

const routes = [
  {
    path: '/',
    redirect: '/notes'
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/features/auth/LoginPage.vue')
  },
  {
    path: '/notes',
    name: 'Notes',
    component: preloadRoute(() => import('@/features/notes/NotesPage.vue')),
    meta: { requiresAuth: true }
  },
  {
    path: '/smart-views',
    name: 'SmartViews',
    component: () => import('@/features/smart-views/SmartViewsPageSimple.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/tags',
    name: 'Tags',
    component: () => import('@/features/tags/TagsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/features/profile/ProfilePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/features/admin/AdminPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/backup',
    name: 'Backup',
    component: () => import('@/features/backup/BackupPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/guide',
    name: 'Guide',
    component: () => import('@/features/guide/GuidePage.vue')
  },
  {
    path: '/feedback',
    name: 'Feedback',
    component: () => import('@/features/feedback/FeedbackPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/calculator',
    name: 'Calculator',
    component: () => import('@/features/calculator/CalculatorPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/funds',
    name: 'Funds',
    component: () => import('@/features/funds/FundsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/sessions',
    name: 'Sessions',
    component: () => import('@/features/sessions/SessionsPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/features/NotFoundPage.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.meta.requiresAuth
  
  // Quick check - if already loaded, proceed immediately
  if (!authState.loading) {
    const isAuthenticated = authState.currentUser !== null
    
    if (requiresAuth && !isAuthenticated) {
      next('/login')
    } else if (to.path === '/login' && isAuthenticated) {
      next('/notes')
    } else {
      next()
    }
    return
  }
  
  // Wait for auth initialization with timeout
  const authPromise = new Promise((resolve) => {
    const checkAuth = () => {
      if (!authState.loading) {
        resolve()
      } else {
        setTimeout(checkAuth, 50)
      }
    }
    checkAuth()
  })
  
  const timeoutPromise = new Promise(resolve => setTimeout(resolve, 2000))
  await Promise.race([authPromise, timeoutPromise])
  
  const isAuthenticated = authState.currentUser !== null
  
  if (requiresAuth && !isAuthenticated) {
    next('/login')
  } else if (to.path === '/login' && isAuthenticated) {
    next('/notes')
  } else {
    next()
  }
})

export default router
